import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Card, Button, Form, Alert, Spinner, Badge, Row, Col } from 'react-bootstrap';
import { Trash2, Plus, Edit2, Save } from 'lucide-react';
import api from '../services/api';

const QuizEditor = () => {
    const { courseId, lessonId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [quiz, setQuiz] = useState({
        title: '',
        description: '',
        time_limit_minutes: null,
        passing_score: 60,
        questions: []
    });
    const [editingQuestion, setEditingQuestion] = useState(null);
    const [showAddQuestion, setShowAddQuestion] = useState(false);

    useEffect(() => {
        fetchQuiz();
    }, [lessonId]);

    const fetchQuiz = async () => {
        try {
            const response = await api.get(`/lessons/${lessonId}/quiz_edit/`);
            if (response.data) {
                setQuiz(response.data);
            }
        } catch (err) {
            if (err.response?.status === 404) {
                console.log('Quiz nie istnieje, zostanie utworzony');
            } else {
                setError('Nie udało się pobrać quizu');
            }
        } finally {
            setLoading(false);
        }
    };

    const handleQuizSave = async () => {
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            const data = {
                title: quiz.title,
                description: quiz.description,
                time_limit_minutes: quiz.time_limit_minutes,
                passing_score: quiz.passing_score
            };
            await api.post(`/lessons/${lessonId}/create_quiz/`, data);
            setSuccess('✅ Quiz zapisany!');
            fetchQuiz();
            navigate(`/courses/${courseId}/lessons/${lessonId}`)
        } catch (err) {
            setError('Błąd zapisu quizu');
        } finally {
            setSaving(false);
        }
    };

    const handleAddQuestion = async (questionData) => {
        setSaving(true);
        setError('');
        setSuccess('');

        try {
            let quizId = quiz?.id;
            // Jeśli quiz nie istnieje tworze go automatycznie
            if (!quizId) {

                const quizData = {
                    title: quiz.title || 'Nowy quiz',
                    description: quiz.description || '',
                    time_limit_minutes: quiz.time_limit_minutes || null,
                    passing_score: quiz.passing_score || 60
                };

                const quizResponse = await api.post(`/lessons/${lessonId}/create_quiz/`, quizData);
                quizId = quizResponse.data.id;

                // Odśwież quiz
                await fetchQuiz();
                setSuccess('✅ Quiz został automatycznie utworzony!');
            }

            const response = await api.post(`/lessons/${lessonId}/add_question/`, questionData);
            setQuiz(prev => ({
                ...prev,
                questions: [...prev.questions, response.data]
            }));
            setShowAddQuestion(false);
            setSuccess('✅ Pytanie dodane!');

        } catch (err) {
            console.error('❌ Błąd:', err);
            setError(err.response?.data?.detail || 'Błąd dodawania pytania');
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteQuestion = async (questionId) => {
        if (!window.confirm('Usunąć pytanie?')) return;

        try {
            await api.delete(`/lessons/${lessonId}/delete_question/?question_id=${questionId}`);
            setQuiz(prev => ({
                ...prev,
                questions: prev.questions.filter(q => q.id !== questionId)
            }));
            setSuccess('✅ Pytanie usunięte!');
        } catch (err) {
            setError('Błąd usuwania pytania');
        }
    };

    if (loading) {
        return (
            <Container className="text-center mt-5">
                <Spinner animation="border" variant="primary" />
            </Container>
        );
    }

    return (
        <Container className="mt-4 mb-5">
            <Button variant="link" onClick={() => navigate(`/courses/${courseId}/lessons/${lessonId}`)}>
                ← Wróć do lekcji
            </Button>

            <Card className="shadow-sm mt-3">
                <Card.Body>
                    <h2>📝 Edytor quizu</h2>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}

                    <Form>
                        <Form.Group className="mb-3">
                            <Form.Label>Tytuł quizu *</Form.Label>
                            <Form.Control
                                type="text"
                                value={quiz.title || ''}
                                onChange={(e) => setQuiz({ ...quiz, title: e.target.value })}
                                placeholder="Nazwa quizu"
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>Opis</Form.Label>
                            <Form.Control
                                as="textarea"
                                rows={2}
                                value={quiz.description || ''}
                                onChange={(e) => setQuiz({ ...quiz, description: e.target.value })}
                                placeholder="Krótki opis quizu..."
                            />
                        </Form.Group>

                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Czas (minuty) - opcjonalnie</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={quiz.time_limit_minutes || ''}
                                        onChange={(e) => setQuiz({ ...quiz, time_limit_minutes: parseInt(e.target.value) || null })}
                                        placeholder="np. 15"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Próg zaliczenia (%)</Form.Label>
                                    <Form.Control
                                        type="number"
                                        value={quiz.passing_score || 60}
                                        onChange={(e) => setQuiz({ ...quiz, passing_score: parseInt(e.target.value) || 60 })}
                                        min="0"
                                        max="100"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Button variant="primary" onClick={handleQuizSave} disabled={saving} >
                            {saving ? 'Zapisywanie...' : '💾 Zapisz quiz'}

                        </Button>
                    </Form>
                </Card.Body>
            </Card>

            {/* Lista pytań */}
            <Card className="shadow-sm mt-4">
                <Card.Body>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4>Pytania ({quiz.questions?.length || 0})</h4>
                        <Button variant="success" onClick={() => setShowAddQuestion(true)}>
                            <Plus size={16} /> Dodaj pytanie
                        </Button>
                    </div>

                    {quiz.questions?.length === 0 && (
                        <Alert variant="info">Brak pytań. Dodaj pierwsze pytanie!</Alert>
                    )}

                    {quiz.questions?.map((q, index) => (
                        <Card key={q.id} className="mb-2">
                            <Card.Body className="d-flex justify-content-between align-items-center">
                                <div>
                                    <strong>Pytanie {index + 1}:</strong> {q.text}
                                    <Badge bg="secondary" className="ms-2">{q.points} pkt</Badge>
                                    <Badge bg="info" className="ms-2">{q.question_type}</Badge>
                                    {q.answers?.length > 0 && (
                                        <div className="mt-1">
                                            <small className="text-muted">
                                                Odpowiedzi: {q.answers.map(a => a.text).join(', ')}
                                            </small>
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Button
                                        variant="outline-danger"
                                        size="sm"
                                        onClick={() => handleDeleteQuestion(q.id)}
                                    >
                                        <Trash2 size={16} />
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    ))}
                </Card.Body>
            </Card>

            {/* Modal dodawania pytania */}
            {showAddQuestion && (
                <AddQuestionModal
                    onSave={handleAddQuestion}
                    onClose={() => setShowAddQuestion(false)}
                />
            )}
        </Container>
    );
};

// Komponent modalny do dodawania pytań
const AddQuestionModal = ({ onSave, onClose }) => {
    const [question, setQuestion] = useState({
        text: '',
        question_type: 'single',
        points: 1,
        answers: [{ text: '', is_correct: false }, { text: '', is_correct: false }]
    });

    const handleAddAnswer = () => {
        setQuestion({
            ...question,
            answers: [...question.answers, { text: '', is_correct: false }]
        });
    };

    const handleAnswerChange = (index, field, value) => {
        const newAnswers = [...question.answers];
        newAnswers[index][field] = value;
        setQuestion({ ...question, answers: newAnswers });
    };

    const handleRemoveAnswer = (index) => {
        if (question.answers.length <= 2) return;
        const newAnswers = question.answers.filter((_, i) => i !== index);
        setQuestion({ ...question, answers: newAnswers });
    };

    const handleSubmit = () => {
        // Walidacja
        if (!question.text.trim()) {
            alert('Wprowadź treść pytania');
            return;
        }
        if (question.question_type !== 'text') {
            const hasCorrect = question.answers.some(a => a.is_correct);
            if (!hasCorrect) {
                alert('Zaznacz przynajmniej jedną poprawną odpowiedź');
                return;
            }
        }
        const dataToSend = {
            text: question.text,
            question_type: question.question_type,
            points: question.points,
            answers: question.answers.map(a => ({
                text: a.text,
                is_correct: a.is_correct
            }))
        };

        onSave(dataToSend);
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-lg">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">➕ Dodaj pytanie</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>
                    <div className="modal-body">
                        <Form>
                            <Form.Group className="mb-3">
                                <Form.Label>Treść pytania *</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={question.text}
                                    onChange={(e) => setQuestion({ ...question, text: e.target.value })}
                                    placeholder="Wprowadź treść pytania..."
                                />
                            </Form.Group>

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Typ pytania</Form.Label>
                                        <Form.Select
                                            value={question.question_type}
                                            onChange={(e) => setQuestion({ ...question, question_type: e.target.value })}
                                        >
                                            <option value="single">Single choice</option>
                                            <option value="multiple">Multiple choice</option>
                                            <option value="text">Tekstowa</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Punkty</Form.Label>
                                        <Form.Control
                                            type="number"
                                            value={question.points}
                                            onChange={(e) => setQuestion({ ...question, points: parseInt(e.target.value) || 1 })}
                                            min="1"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {question.question_type !== 'text' && (
                                <div>
                                    <Form.Label>Odpowiedzi</Form.Label>
                                    {question.answers.map((answer, index) => (
                                        <div key={index} className="d-flex gap-2 mb-2 align-items-center">
                                            <Form.Check
                                                type={question.question_type === 'single' ? 'radio' : 'checkbox'}
                                                name="correct"
                                                label="Poprawna"
                                                checked={answer.is_correct}
                                                onChange={(e) => handleAnswerChange(index, 'is_correct', e.target.checked)}
                                            />
                                            <Form.Control
                                                type="text"
                                                value={answer.text}
                                                onChange={(e) => handleAnswerChange(index, 'text', e.target.value)}
                                                placeholder={`Odpowiedź ${index + 1}`}
                                            />
                                            <Button
                                                variant="outline-danger"
                                                size="sm"
                                                onClick={() => handleRemoveAnswer(index)}
                                                disabled={question.answers.length <= 2}
                                            >
                                                ×
                                            </Button>
                                        </div>
                                    ))}
                                    <Button variant="link" onClick={handleAddAnswer} className="mt-2">
                                        + Dodaj odpowiedź
                                    </Button>
                                </div>
                            )}
                        </Form>
                    </div>
                    <div className="modal-footer">
                        <Button variant="secondary" onClick={onClose}>Anuluj</Button>
                        <Button variant="primary" onClick={handleSubmit}>Dodaj pytanie</Button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default QuizEditor;